import React from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';
import { AdminStatCard } from './components/AdminStatCard';

// ============================================================================
// Types
// ============================================================================

export interface Feedback {
  id: number;
  userEmail: string;
  userId: number;
  rating: number;
  comments: string | null;
  createdAt: string;
}

export interface RatingDistribution {
  fiveStars: number;
  fourStars: number;
  threeStars: number;
  twoStars: number;
  oneStar: number;
}

export interface AdminFeedbacksPageProps {
  feedbacks: Feedback[];
  statistics: {
    total: number;
    withComments: number;
    postponed: number;
  };
  ratingDistribution: RatingDistribution;
  averageRating: number;

  // URLs
  dashboardPath: string;
  feedbackPath: (id: number) => string;
  userPath: (id: number) => string;

  // Labels
  labels: {
    title: string;
    subtitle: string;
    backLabel: string;
    // Stats
    averageRatingLabel: string;
    outOf5Stars: string;
    totalRatingsLabel: string;
    feedbackSubmitted: string;
    withCommentsLabel: string;
    postponedLabel: string;
    delayedFeedback: string;
    // Distribution
    ratingDistributionTitle: string;
    // Table
    recentFeedbackTitle: string;
    userColumn: string;
    ratingColumn: string;
    commentsColumn: string;
    submittedColumn: string;
    actionsColumn: string;
    viewButton: string;
    userButton: string;
    noComments: string;
    agoLabel: string;
    noFeedbackYet: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="d-flex align-items-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`bi ${star <= rating ? 'bi-star-fill text-warning' : 'bi-star text-muted'}`}
        />
      ))}
      <span className="ms-2 badge bg-primary">{rating}</span>
    </div>
  );
}

function RatingDistributionBar({
  stars,
  count,
  total,
  colorClass,
}: {
  stars: number;
  count: number;
  total: number;
  colorClass: string;
}) {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="d-flex align-items-center mb-2">
      <span className="me-2" style={{ width: 80 }}>
        {stars} <i className="bi bi-star-fill text-warning" />
      </span>
      <div className="progress flex-grow-1" style={{ height: 25 }}>
        <div
          className={`progress-bar ${colorClass}`}
          role="progressbar"
          style={{ width: `${percentage}%` }}
        >
          {count}
        </div>
      </div>
      <span className="ms-2" style={{ width: 50 }}>
        {Math.round(percentage)}%
      </span>
    </div>
  );
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminFeedbacksPage({
  feedbacks,
  statistics,
  ratingDistribution,
  averageRating,
  dashboardPath,
  feedbackPath,
  userPath,
  labels,
}: AdminFeedbacksPageProps) {
  const commentsPercentage =
    statistics.total > 0
      ? ((statistics.withComments / statistics.total) * 100).toFixed(1)
      : '0';

  return (
    <div>
      <AdminPageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        icon="chat-heart"
        backLink={{ href: dashboardPath, label: labels.backLabel }}
      />

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.averageRatingLabel}
            value={averageRating}
            subtitle={labels.outOf5Stars}
            icon="star-fill"
            iconColor="warning"
            borderColor="warning"
            valueColor="warning"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.totalRatingsLabel}
            value={statistics.total}
            subtitle={labels.feedbackSubmitted}
            icon="chat-square-heart"
            iconColor="primary"
            borderColor="primary"
            valueColor="primary"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.withCommentsLabel}
            value={statistics.withComments}
            subtitle={`${commentsPercentage}%`}
            icon="chat-text"
            iconColor="success"
            borderColor="success"
            valueColor="success"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.postponedLabel}
            value={statistics.postponed}
            subtitle={labels.delayedFeedback}
            icon="clock-history"
            iconColor="secondary"
            borderColor="secondary"
            valueColor="secondary"
          />
        </div>
      </div>

      {/* Rating Distribution */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <i className="bi bi-bar-chart me-2" />
            {labels.ratingDistributionTitle}
          </h5>
          <div className="mb-3">
            <RatingDistributionBar
              stars={5}
              count={ratingDistribution.fiveStars}
              total={statistics.total}
              colorClass="bg-warning"
            />
            <RatingDistributionBar
              stars={4}
              count={ratingDistribution.fourStars}
              total={statistics.total}
              colorClass="bg-info"
            />
            <RatingDistributionBar
              stars={3}
              count={ratingDistribution.threeStars}
              total={statistics.total}
              colorClass="bg-primary"
            />
            <RatingDistributionBar
              stars={2}
              count={ratingDistribution.twoStars}
              total={statistics.total}
              colorClass="bg-warning"
            />
            <RatingDistributionBar
              stars={1}
              count={ratingDistribution.oneStar}
              total={statistics.total}
              colorClass="bg-danger"
            />
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <h5 className="mb-0">
            <i className="bi bi-list-ul me-2" />
            {labels.recentFeedbackTitle}
          </h5>
        </div>
        <div className="card-body p-0">
          {feedbacks.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{labels.userColumn}</th>
                    <th>{labels.ratingColumn}</th>
                    <th>{labels.commentsColumn}</th>
                    <th>{labels.submittedColumn}</th>
                    <th>{labels.actionsColumn}</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.map((feedback) => (
                    <tr key={feedback.id}>
                      <td>
                        <a
                          href={userPath(feedback.userId)}
                          className="text-decoration-none"
                        >
                          <strong>{feedback.userEmail}</strong>
                        </a>
                      </td>
                      <td>
                        <StarRating rating={feedback.rating} />
                      </td>
                      <td>
                        {feedback.comments ? (
                          <div
                            className="text-truncate"
                            style={{ maxWidth: 300 }}
                            title={feedback.comments}
                          >
                            {feedback.comments}
                          </div>
                        ) : (
                          <span className="text-muted fst-italic">{labels.noComments}</span>
                        )}
                      </td>
                      <td>
                        {formatTimeAgo(feedback.createdAt)} {labels.agoLabel}
                        <br />
                        <small className="text-muted">{formatDate(feedback.createdAt)}</small>
                      </td>
                      <td>
                        <a
                          href={feedbackPath(feedback.id)}
                          className="btn btn-sm btn-primary me-2"
                        >
                          <i className="bi bi-eye" /> {labels.viewButton}
                        </a>
                        <a
                          href={userPath(feedback.userId)}
                          className="btn btn-sm btn-outline-secondary"
                        >
                          <i className="bi bi-person" /> {labels.userButton}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-chat-heart" style={{ fontSize: '3rem' }} />
              <p className="mt-3 mb-0">{labels.noFeedbackYet}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminFeedbacksPage;
